import { create } from 'zustand';
import { syncService } from './syncService';
import { useAppStore } from './store';
import { useConfigStore } from './useConfigStore';
import { logAuthEvent } from './apis';


interface AppUser {
    id: string;
    name: string;
    email: string;
    picture: string;
    user_plan: "free" | "pro";
}

interface AuthState {
    user: AppUser | null;
    isLoading: boolean;
    isInitialized: boolean;

    // Actions
    setUser: (user: AppUser | null) => void;
    setLoading: (loading: boolean) => void;
    initializeAuth: () => Promise<void>;
    signOut: () => Promise<void>;
    loadUserFromStorage: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
    user: null,
    isLoading: false,
    isInitialized: false,

    setUser: (user) => set({ user }),

    setLoading: (loading) => set({ isLoading: loading }),

    // Load user from localStorage
    loadUserFromStorage: () => {
        const savedUser = localStorage.getItem('pomozid_user');
        if (savedUser) {
            try {
                set({ user: JSON.parse(savedUser) });
            } catch {
                localStorage.removeItem('pomozid_user');
            }
        }
    },

    // Initialize auth - fetch profile if token exists
    initializeAuth: async () => {
        const { config } = useConfigStore.getState()
        if (!config) {
            throw new Error('Config not loaded');
        }

        const token = localStorage.getItem('pomozid_token');
        if (!token) {
            set({ isInitialized: true });
            return;
        }

        try {
            const res = await fetch(`${config.AUTH_PROFILE_URL}`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (!res.ok) throw new Error('Failed to fetch user profile');

            const data = await res.json();
            const userData: AppUser = {
                id: data.uid,
                name: data.displayName,
                email: data.email,
                picture: data.photoURL,
                user_plan: data.user_plan || "free",
            };

            set({ user: userData, isInitialized: true });
            localStorage.setItem('pomozid_user', JSON.stringify(userData));

            // Load user data from Firestore after auth
            await useAppStore.getState().initialize();

        } catch (err) {
            console.error('Error loading profile:', err);
            // Clear invalid token
            localStorage.removeItem('pomozid_token');
            localStorage.removeItem('pomozid_user');
            set({ user: null, isInitialized: true });
        }
    },

    // Sign out
    signOut: async () => {
        const { user } = get();

        // Stop auto-sync before logout
        console.log('⏸️ Stopping sync...');
        syncService.stopAutoSync();

        set({ user: null });
        localStorage.removeItem('pomozid_user');
        localStorage.removeItem('pomozid_token');

        if (user) {
            await logAuthEvent('signout', { email: user.email, userId: user.id });
        }
    },
}));